import { INDICATORS_BY_ID, type IndicatorDef } from '@/lib/indicators';
import type { IndicatorSnapshot } from '@/lib/types';
import { formatValue, signed } from '@/lib/format';
import { getHistoricalValue } from '@/lib/store';
import { isPointMove } from '@/lib/changeDetection';
import { DataStatus, timeAgo } from './DataStatus';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ExternalLink } from 'lucide-react';

const DAY = 24 * 60 * 60 * 1000;

/**
 * Signed change of an indicator over a lookback window, or null if unknown.
 * For point-mode indicators (percentage-unit / zero-centered) `pct` is left
 * null — a ratio "% of a %" is meaningless there (e.g. btc_trend 0.4%→1.2% is
 * a +0.8 POINT move, not "+200%"). Kept consistent with change detection.
 */
function windowChange(
  def: IndicatorDef,
  current: number | null,
  windowMs: number,
): { abs: number; pct: number | null } | null {
  if (current === null) return null;
  const prev = getHistoricalValue(def.id, windowMs);
  if (!prev || prev.value === null) return null;
  const abs = current - prev.value;
  const pct =
    isPointMove(def.id) || prev.value === 0
      ? null
      : (abs / Math.abs(prev.value)) * 100;
  return { abs, pct };
}

/** Plain-language trend descriptor based on direction-adjusted 30d move. */
function trendLabel(
  def: IndicatorDef,
  current: number | null,
): { label: string; className: string } {
  const c = windowChange(def, current, 30 * DAY);
  // "Stable" threshold: for point-mode indicators judge the absolute point
  // move (< ~0.5 pt is flat); for level indicators judge the % move (< 1%).
  const magnitude = c ? (c.pct === null ? Math.abs(c.abs) : Math.abs(c.pct)) : 0;
  const flatCutoff = c && c.pct === null ? 0.5 : 1;
  if (!c || magnitude < flatCutoff) {
    return { label: 'Stable', className: 'text-muted-foreground' };
  }
  // Direction-adjusted: is the raw move supportive for the score?
  const supportive = def.direction === 1 ? c.abs > 0 : c.abs < 0;
  const rising = c.abs > 0;
  return {
    label: rising ? (supportive ? 'Improving' : 'Weakening') : supportive ? 'Improving' : 'Weakening',
    className: supportive ? 'text-primary' : 'text-red-400',
  };
}

/**
 * A short, honest "what would improve the signal?" line derived from the
 * indicator's direction. This is an explanation of the deterministic model —
 * never a price prediction.
 */
function improvementHint(def: IndicatorDef): string {
  const dirWord = def.direction === 1 ? 'higher' : 'lower';
  return `A sustained move ${dirWord} in ${def.short}, confirmed over multiple readings, would strengthen this indicator's contribution to the score.`;
}

// Plain-language bands for the 0–100 Strait of Hormuz risk index so a bare
// number like "12.7" is interpretable.
const HORMUZ_BANDS: {
  min: number;
  label: string;
  blurb: string;
  className: string;
  dot: string;
}[] = [
  {
    min: 0,
    label: 'Calm',
    blurb: 'Oil markets are quiet — little to no chokepoint stress is priced in.',
    className: 'text-primary',
    dot: 'bg-primary',
  },
  {
    min: 25,
    label: 'Elevated',
    blurb: 'Oil is firming and/or the seaborne spread is widening — worth watching.',
    className: 'text-yellow-300',
    dot: 'bg-yellow-400',
  },
  {
    min: 50,
    label: 'Stressed',
    blurb: 'Markets are pricing meaningful supply risk through the strait.',
    className: 'text-orange-400',
    dot: 'bg-orange-500',
  },
  {
    min: 75,
    label: 'Acute',
    blurb: 'Severe disruption risk is priced in — a strong risk-off energy signal.',
    className: 'text-red-400',
    dot: 'bg-red-500',
  },
];

function hormuzBand(value: number) {
  return [...HORMUZ_BANDS].reverse().find((b) => value >= b.min) ?? HORMUZ_BANDS[0];
}

export function IndicatorDetailDialog({
  snapshot,
  open,
  onOpenChange,
}: {
  snapshot: IndicatorSnapshot | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  if (!snapshot) return null;
  const def = INDICATORS_BY_ID[snapshot.indicatorId];
  if (!def) return null;

  const changes: { label: string; window: number }[] = [
    { label: '24h', window: DAY },
    { label: '7d', window: 7 * DAY },
    { label: '30d', window: 30 * DAY },
  ];
  const trend = trendLabel(def, snapshot.value);

  const fmtChange = (c: { abs: number; pct: number | null } | null): string => {
    if (!c) return '—';
    // Point-mode (percentage-unit) indicators: show an absolute POINT move,
    // never a ratio "% of a %". Level indicators: show abs + (% change).
    if (isPointMove(def.id)) {
      return `${signed(c.abs, 1)} pts`;
    }
    const absStr = signed(c.abs, def.unit === 'sats' ? 5 : 2);
    return `${absStr}${c.pct !== null ? ` (${signed(c.pct, 1)}%)` : ''}`;
  };
  const changeClass = (c: { abs: number; pct: number | null } | null): string => {
    if (!c) return '';
    return (def.direction === 1 ? c.abs >= 0 : c.abs <= 0)
      ? 'text-primary'
      : 'text-red-400';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6 text-left">
            {def.label}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Detailed breakdown of the {def.label} indicator: current value,
            recent changes, sub-score, and data source.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {snapshot.quality === 'unavailable' && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
              ⚠ DATA UNAVAILABLE — this indicator is not contributing to the
              current score. Confidence is reduced accordingly.
            </div>
          )}
          {snapshot.quality === 'stale' && snapshot.timestamp === 0 && (
            <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-200">
              ⚠ REFERENCE BASELINE — a live reading isn&apos;t available right
              now, so an indicative baseline is shown. Treat this value as
              approximate, not current.
            </div>
          )}
          {snapshot.quality === 'stale' && snapshot.timestamp !== 0 && (
            <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-200">
              ⚠ AWAITING UPDATE — this is a real reading from{' '}
              {timeAgo(snapshot.timestamp)}. This series publishes
              infrequently ({def.frequency}), so the latest value can be older
              than others while still being the most current available.
            </div>
          )}

          {/* current + trend */}
          <div className="grid grid-cols-2 gap-2 text-center">
            <Stat label="Current" value={formatValue(def, snapshot.value)} />
            <Stat label="Trend" value={trend.label} valueClass={trend.className} />
          </div>

          {/* multi-window change grid */}
          <div className="grid grid-cols-3 gap-2 text-center">
            {changes.map(({ label, window }) => {
              const c = windowChange(def, snapshot.value, window);
              return (
                <Stat
                  key={label}
                  label={label}
                  value={fmtChange(c)}
                  valueClass={changeClass(c)}
                />
              );
            })}
          </div>

          {/* Hormuz risk scale */}
          {def.id === 'hormuz_risk' && snapshot.value !== null && (
            <HormuzScale value={snapshot.value} />
          )}

          {/* sub-score */}
          <div className="rounded-lg border border-border/60 bg-secondary/40 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Sub-score</span>
              <span className="font-mono font-semibold">{snapshot.subScore}/100</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${snapshot.subScore}%` }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Signal contribution:{' '}
                <span
                  className={cn(
                    'font-mono font-semibold',
                    snapshot.contribution >= 0 ? 'text-primary' : 'text-red-400',
                  )}
                >
                  {signed(snapshot.contribution)} pts
                </span>
              </span>
              <span>weight ×{def.weight}</span>
            </div>
          </div>

          <Section title="Why it matters">{def.why}</Section>
          <Section title="How it affects the score">{def.mechanism}</Section>
          <Section title="What would improve the signal?">
            {improvementHint(def)}
          </Section>

          <div className="flex items-center justify-between rounded-lg border border-border/60 p-3 text-xs">
            <div>
              <p className="text-muted-foreground">Source</p>
              <p className="font-medium">{def.source}</p>
            </div>
            <div className="text-right">
              <p className="text-muted-foreground">Updated</p>
              <div className="flex items-center justify-end gap-2">
                <DataStatus quality={snapshot.quality} showLabel={false} />
                <span className="font-medium">{timeAgo(snapshot.timestamp)}</span>
              </div>
            </div>
          </div>

          {def.sourceUrl && (
            <a
              href={def.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              View source <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HormuzScale({ value }: { value: number }) {
  const band = hormuzBand(value);
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="rounded-lg border border-border/60 bg-secondary/40 p-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Risk level</span>
        <span className={cn('flex items-center gap-1.5 font-semibold', band.className)}>
          <span className={cn('h-2 w-2 rounded-full', band.dot)} />
          {band.label} · {value.toFixed(1)}/100
        </span>
      </div>

      {/* gradient scale with marker */}
      <div className="relative mt-2 h-2 overflow-hidden rounded-full bg-gradient-to-r from-primary via-yellow-400 to-red-500">
        <div
          className="absolute top-1/2 h-3.5 w-1 -translate-y-1/2 rounded-full bg-foreground shadow ring-1 ring-background"
          style={{ left: `calc(${pct}% - 2px)` }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
        <span>Calm</span>
        <span>Elevated</span>
        <span>Stressed</span>
        <span>Acute</span>
      </div>

      <p className="mt-2 text-xs text-foreground/80">{band.blurb}</p>

      <p className="mt-3 rounded-md border border-yellow-500/30 bg-yellow-500/5 p-2 text-[11px] leading-relaxed text-yellow-200/90">
        ⚠ This reflects <span className="font-semibold">oil-market pricing only</span>.
        It does not track live shipping, conflict, or strait-closure events and can
        lag a real crisis. A low reading means oil markets are calm — not that the
        strait is safe.
      </p>
    </div>
  );
}

function Stat({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-card/60 p-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={cn('mt-1 font-mono text-sm font-semibold', valueClass)}>
        {value}
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <p className="mt-1 text-sm text-foreground/90">{children}</p>
    </div>
  );
}
